import { forwardRef, type PropsWithChildren } from 'react';
import styled from 'styled-components';

import HoverableText from '#@/components/HoverableText';
import Icon from '#@/components/Icon';

import HoverableElement from '../HoverableElement';
import Spinner from '../Spinner';
import {
  ICON_SIZE_DEFAULT,
  ICON_SIZE_LARGE_DEFAULT,
  SPINNER_SIZE,
  SPINNER_SIZE_LARGE,
} from './constants';
import { tagStyles } from './styles';
import type { TagProps } from './types';

const TagBase = styled.div`
  ${tagStyles}
`;

export const Tag = forwardRef<HTMLDivElement, PropsWithChildren<TagProps>>(
  ({ children, leftIcon, color, large, truncate = true, showSpinner, tooltip, ...props }, ref) => {
    const getContent = () => {
      if (truncate) {
        return (
          <HoverableText text={children} textWhenNotTruncated={tooltip}>
            {children}
          </HoverableText>
        );
      }

      return tooltip ? (
        <HoverableElement isTriggerElementClickable={false} tooltipText={tooltip}>
          {children}
        </HoverableElement>
      ) : (
        <span>{children}</span>
      );
    };

    return (
      <TagBase ref={ref} color={color} large={large} truncate={truncate} {...props}>
        {leftIcon && !showSpinner && (
          <Icon
            {...leftIcon}
            size={leftIcon.size || (large ? ICON_SIZE_LARGE_DEFAULT : ICON_SIZE_DEFAULT)}
          />
        )}
        {showSpinner && <Spinner color={color} size={large ? SPINNER_SIZE_LARGE : SPINNER_SIZE} />}
        {getContent()}
      </TagBase>
    );
  },
);

Tag.displayName = 'Tag';
